"use client";

import { Button } from "@/components/ui/button";
import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, sectionVariants, SpacingType } from "@/types";
import Image from '@/components/ui/Image';
import { useVideo, VideoIframe } from "../components/VideoIframe";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

import parse from "html-react-parser";
import Link from "next/link";
import { SectionWrapper } from "../components/SectionWraper";
interface Testimonial2Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  testimonials?: {
    mainTestimonial?: {
      content?: string;
      mediaType?: "image" | "video";
      image?: string;
      imageAltText?: string;
      video?: string;
      author?: string;
      company?: string;
      hasButton?: boolean;
      buttonText?: string;
      buttonLink?: string;
      buttonStyle?: ButtonStyleType;
    };
    testimonials?: {
      image?: string;
      imageAltText?: string;
      content?: string;
      author?: string;
      company?: string;
      link?: string;
      linkText?: string;
      initials?: string;
    }[];
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Testimonial2 = ({
  darkMode = false,
  spacing = 'xlarge',
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Testimonial 2",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  testimonials,
  attributes,
}: Testimonial2Props) => {
  const { isVideoOpen, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const itemBackgroundColors = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="testimonial2"
      attributes={attributes}
    > 
      <div className="container">
        <div className="mb-10 md:mb-20" data-aos="fade-up">
          <div>
            {title && (
              <h2 className="text-center sm:text-left sm:max-w-[500px] lg:max-w-[800px] mx-auto sm:mx-0">
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="mt-6 paragraph-lg text-muted-foreground text-center sm:text-left">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <Button asChild variant={buttonStyle} size="lg" className="mt-6 w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>  
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <div className="mt-2 paragraph-sm text-muted-foreground text-center sm:text-left">
                    {microCopy}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-10 md:gap-20">
          {testimonials?.mainTestimonial && (
            testimonials.mainTestimonial.buttonLink ? (
              <a href={testimonials.mainTestimonial.buttonLink} className="grid grid-cols-1 items-stretch gap-x-0 gap-y-4 md:grid-cols-3 lg:gap-4" data-aos="fade-up" data-aos-delay="100">
                {testimonials.mainTestimonial.mediaType === "image" && testimonials.mainTestimonial.image && (
                  <Image  
                    src={testimonials.mainTestimonial.image}
                    alt={testimonials.mainTestimonial.imageAltText ?? ""}
                    title={testimonials.mainTestimonial.imageAltText ?? ""}
                    width={800}
                    height={800}
                    className="w-full h-full rounded-xl object-cover"
                  />
                )}
                {testimonials.mainTestimonial.mediaType === "video" && testimonials.mainTestimonial.video && (
                  <div 
                    className="relative group/testimonial2 cursor-pointer w-full rounded-xl overflow-hidden" 
                    onClick={() => handleVideoClick(testimonials.mainTestimonial!.video!)}
                  >
                    <Image  
                      src={testimonials.mainTestimonial.image || getVideoThumbnail(testimonials.mainTestimonial.video) || ""}
                      alt={testimonials.mainTestimonial.imageAltText ?? ""}
                      title={testimonials.mainTestimonial.imageAltText ?? ""}
                      width={800}
                      height={800}
                      className="w-full h-full rounded-xl object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex items-center justify-center w-12 h-12">
                        <Button 
                          variant="secondary" 
                          size="iconLg"   
                          className="absolute bottom-6 left-6 bg-muted rounded-full"
                        >
                          <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                        </Button>
                      </div>
                    </div>
                    <VideoIframe 
                      videoUrl={testimonials.mainTestimonial.video} 
                      isOpen={isVideoOpen} 
                      onClose={handleCloseVideo} 
                    />
                  </div>
                )}

                <Card className="col-span-2 flex px-0 md:px-6 py-0 md:py-6 border-none bg-transparent">
                  <div className="flex flex-col gap-6 flex-1 justify-center">
                    {testimonials.mainTestimonial.content && (
                      <h5>
                        {parse(testimonials.mainTestimonial.content)}
                      </h5>
                    )}
                    {testimonials.mainTestimonial.author && (
                      <div className="flex flex-col items-start paragraph-lg">
                        <p className="font-bold">{testimonials.mainTestimonial.author}</p>
                        <p className="text-muted-foreground">{testimonials.mainTestimonial.company}</p>
                      </div>
                    )}
                  </div>
                  {testimonials.mainTestimonial.hasButton && (
                    <div className="flex">
                      <Button variant={testimonials.mainTestimonial.buttonStyle} size="lg" className="w-full sm:w-auto cursor-pointer">  
                        {testimonials.mainTestimonial.buttonText}
                      </Button>
                    </div>
                  )}
                </Card>
              </a>
            ) : (
              <div className="grid grid-cols-1 items-stretch gap-x-0 gap-y-4 md:grid-cols-3 lg:gap-4" data-aos="fade-up" data-aos-delay="100">
                {testimonials.mainTestimonial.mediaType === "image" && testimonials.mainTestimonial.image && (
                  <Image  
                    src={testimonials.mainTestimonial.image}
                    alt={testimonials.mainTestimonial.imageAltText ?? ""}
                    title={testimonials.mainTestimonial.imageAltText ?? ""}
                    width={800}
                    height={800}
                    className="w-full h-full rounded-xl object-cover"
                  />
                )}
                {testimonials.mainTestimonial.mediaType === "video" && testimonials.mainTestimonial.video && (
                  <div 
                    className="relative group/testimonial2 cursor-pointer w-full rounded-xl overflow-hidden" 
                    onClick={() => handleVideoClick(testimonials.mainTestimonial!.video!)}
                  >
                    <Image  
                      src={testimonials.mainTestimonial.image || getVideoThumbnail(testimonials.mainTestimonial.video) || ""}
                      alt={testimonials.mainTestimonial.imageAltText ?? ""}
                      title={testimonials.mainTestimonial.imageAltText ?? ""}
                      width={800}
                      height={800}
                      className="w-full h-full rounded-xl object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex items-center justify-center w-12 h-12">
                        <Button 
                          variant="secondary" 
                          size="iconLg"   
                          className="absolute bottom-6 left-6 bg-muted rounded-full"
                        >
                          <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                        </Button>
                      </div>
                    </div>
                    <VideoIframe 
                      videoUrl={testimonials.mainTestimonial.video} 
                      isOpen={isVideoOpen} 
                      onClose={handleCloseVideo} 
                    />
                  </div>
                )}

                <Card className="col-span-2 flex px-0 md:px-6 py-0 md:py-6 border-none bg-transparent">
                  <div className="flex flex-col gap-6 flex-1 justify-center">
                    {testimonials.mainTestimonial.content && (
                      <h5>
                        {parse(testimonials.mainTestimonial.content)}
                      </h5>
                    )}
                    {testimonials.mainTestimonial.author && (
                      <div className="flex flex-col items-start paragraph-lg">
                        <p className="font-bold">{testimonials.mainTestimonial.author}</p>
                        <p className="text-muted-foreground">{testimonials.mainTestimonial.company}</p>
                      </div>
                    )}
                  </div>
                  {testimonials.mainTestimonial.hasButton && (
                    <div className="flex">
                      <Button variant={testimonials.mainTestimonial.buttonStyle} size="lg" className="w-full sm:w-auto cursor-pointer">
                        {testimonials.mainTestimonial.buttonText}
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            )
          )}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-aos="fade-up" data-aos-delay="200">
            {testimonials?.testimonials?.slice(0, 2).map((testimonial, index) => (
              testimonial.link ? (
                <a href={testimonial.link} key={index} className="cursor-pointer flex">
                  <Card className={cn(itemBackgroundColors, "border-none")}>
                    {testimonial.content && (
                      <CardContent className="p-0 flex-1 paragraph-lg text-muted-foreground">
                        {parse(testimonial.content)}
                      </CardContent>
                    )}
                    <CardFooter className="p-0">
                      <div className="flex gap-4 flex-1 items-center">
                        {testimonial.image && !testimonial.initials && (
                          <Image
                            src={testimonial.image}
                            alt={testimonial.imageAltText ?? ""}
                            title={testimonial.imageAltText ?? ""}
                            width={48}
                            height={48}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        {testimonial.initials && (
                          <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center">
                            <p className="text-white font-bold paragraph-lg !p-0">{testimonial.initials}</p>
                          </div>
                        )}
                        {testimonial.author && (
                          <div className="flex flex-col paragraph-lg flex-1">
                            <p className="font-bold">{testimonial.author}</p>
                            {testimonial.company && (
                              <p className="text-muted-foreground">{testimonial.company}</p>
                            )}
                          </div>
                        )}

                        <Button variant="outline" lifted grayBorder size="iconLg" className="cursor-pointer">
                          <span className="material-symbols-outlined">arrow_forward</span>
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                </a>
              ) : (
                <Card key={index} className={cn(itemBackgroundColors, "border-none")}>
                  {testimonial.content && (
                    <CardContent className="p-0 flex-1 paragraph-lg text-muted-foreground">
                      {parse(testimonial.content)}
                    </CardContent>
                  )}
                  <CardFooter className="p-0">
                    <div className="flex gap-4 flex-1 items-center">
                      {testimonial.image && !testimonial.initials && (
                        <Image
                          src={testimonial.image}
                          alt={testimonial.imageAltText ?? ""}
                          title={testimonial.imageAltText ?? ""}
                          width={48}
                          height={48}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      {testimonial.initials && (
                        <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center">
                          <p className="text-white font-bold paragraph-lg !p-0">{testimonial.initials}</p>
                        </div>
                      )}
                      {testimonial.author && (
                        <div className="flex flex-col paragraph-lg flex-1">
                          <p className="font-bold">{testimonial.author}</p>
                          {testimonial.company && (
                            <p className="text-muted-foreground">{testimonial.company}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              )
              
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Testimonial2 };
